'use client'

import {InformationLinkButtonSectionFieldsFragment} from '@generated/graphql';
import {FC} from 'react';
import {
  StyledInformationLinkButton,
  StyledInformationLinkButtonHeading,
  StyledInformationLinkButtonBreadTextContainer,
  StyledInformationLinkButtonContainer,
  StyledInformationLinkButtonMediaContainer,
  StyledInformationLinkButtonSectionContainer,
  StyledInformationLinkButtonSectionInnerContainer
} from '@/components/sections/information-link-button-section/InformationLinkButtonSection.styles';
import {RichTextRenderer} from '@/components/contentful/RichTextRenderer';
import {Document} from '@contentful/rich-text-types';
import {Container, Typography} from '@mui/material';
import {asVariant} from '@/lib/typography-variant';
import ContentfulImage from '@/components/ui/image/ContentfulImage';
import Video from '@/components/ui/video/Video';
import {isRenderableLinkButton} from '@/lib/ensure-link-button';
import ConditionalLinkButton from '@/components/ui/buttons/ConditionalLinkButton';

type InformationLinkButtonSectionProps = {
    section: InformationLinkButtonSectionFieldsFragment;
};

type Alignment = 'left' | 'center' | 'right';

const isAlignment = (v: unknown): v is Alignment =>
  v === 'left' || v === 'center' || v === 'right';

const alignItemsFor: Record<Alignment, 'flex-start' | 'center' | 'flex-end'> = {
  left: 'flex-start',
  center: 'center',
  right: 'flex-end',
};

export const InformationLinkButtonSection: FC<InformationLinkButtonSectionProps> = ({ section }) => {
  const mediaUrl = section.media?.url;
  const isVideo = Boolean(section.media?.contentType?.toLowerCase().includes('video'));
  const headingSize = section.headingSize;
  const subheadingSize = section.subheadingSize;
  const alignment: Alignment = isAlignment(section.alignment) ? section.alignment : 'center';
  const linkButtons = section.linkButtonsCollection?.items || [];
  const hasAnyLinkButtons = linkButtons.some(
    (b) =>
      (b?.__typename === 'LinkButton' && isRenderableLinkButton(b)) ||
      (b?.__typename === 'MediaUpload' &&
        !!b.mediaFilesCollection?.items?.[0]?.url &&
        !!(b.heading ?? b.title))
  );

  return (
    <StyledInformationLinkButtonSectionContainer {...section.id && { id: section.id }}>
      <Container maxWidth={'md'}>
        <StyledInformationLinkButtonSectionInnerContainer style={{ textAlign: alignment, alignItems: alignItemsFor[alignment] }}>
          {section.heading && (
            <StyledInformationLinkButtonHeading variant={asVariant(headingSize, 'h2')}>
              {section.heading}
            </StyledInformationLinkButtonHeading>
          )}
          {section.subheading && (
            <Typography sx={{ marginBottom: 1}} variant={asVariant(subheadingSize, 'h3')}>
              {section.subheading}
            </Typography>
          )}
          {mediaUrl && (
            <StyledInformationLinkButtonMediaContainer>
              {isVideo ? (
                <Video
                  src={mediaUrl}
                  title={section.media?.title ?? section.heading ?? undefined}
                  fillViewport={false}
                />
              ) : (
                <ContentfulImage
                  src={mediaUrl}
                  alt={section.heading || ''}
                  fill
                  sizes='100vw'
                  style={{ objectFit: 'cover' }}
                  focalY={section.focalY}
                  focalX={section.focalX}
                />
              )}
            </StyledInformationLinkButtonMediaContainer>
          )}
          {section.breadText && (
            <StyledInformationLinkButtonBreadTextContainer>
              <RichTextRenderer richTextDocument={section.breadText.json as unknown as Document} />
            </StyledInformationLinkButtonBreadTextContainer>
          )}
        </StyledInformationLinkButtonSectionInnerContainer>
        {hasAnyLinkButtons && (
          <StyledInformationLinkButtonContainer style={{ marginInline: section.alignment === 'left' ? 'left' : 'auto'}}>
            {linkButtons.map((item, i) => {
              if (!item) return null;

              if (item.__typename === 'LinkButton') {
                if (!isRenderableLinkButton(item)) return null;
                return (
                  <ConditionalLinkButton
                    key={item.label}
                    button={item}
                    ButtonComponent={StyledInformationLinkButton}
                  />
                );
              }

              if (item.__typename === 'MediaUpload') {
                const url = item.mediaFilesCollection?.items?.[0]?.url;
                const label = item.heading ?? item.title;
                if (!url || !label) return null;
                return (
                  <StyledInformationLinkButton
                    key={`media-${url}-${i}`}
                    component='a'
                    href={url}
                    target='_blank'
                    rel='noopener noreferrer'
                    variant='contained'
                    disableElevation
                  >
                    {label}
                  </StyledInformationLinkButton>
                );
              }

              return null;
            })}
          </StyledInformationLinkButtonContainer>
        )}
      </Container>
    </StyledInformationLinkButtonSectionContainer>
  );
};
